import { useState } from 'react';
import type { Practice, Settings } from '../types';
import { Chips } from '../ui/parts';
import { Customize } from './Customize';
import { buildTimeline, formatLength } from '../lib/timeline';
import { loadSettings, saveSettings } from '../lib/storage';
import styles from './screens.module.css';

/** Length chips only where they make sense: rounds for round-based asana, minutes for the free timer. */
function lengthOptions(p: Practice, s: Settings): { kind: 'rounds' | 'minutes'; options: { value: number; label: string }[] } | null {
  if (p.roundsAdjustable) {
    const base = p.rounds >= 8 ? [4, 8, 12] : [1, 2, 3];
    const values = Array.from(new Set([...base, s.rounds])).sort((a, b) => a - b);
    return {
      kind: 'rounds',
      options: values.map(n => ({
        value: n,
        label: `${n} ${n === 1 ? 'round' : 'rounds'} · ${formatLength(buildTimeline(p, { ...s, rounds: n }).total)}`,
      })),
    };
  }
  if (p.free) {
    return { kind: 'minutes', options: [10, 20, 30].map(m => ({ value: m, label: `${m} min` })) };
  }
  return null;
}

export function describe(p: Practice, s: Settings): string {
  const poses = p.steps.filter(x => x.kind === 'pose');
  if (poses.length) {
    const lengths = new Set(poses.map((_, i) => buildTimeline(p, { ...s, rounds: 1 }).segments.filter(seg => seg.stepIndex === i)[0]?.seconds));
    const pacing = lengths.size === 1 ? `${[...lengths][0]}s per pose` : 'natural pacing';
    return p.roundsAdjustable ? `${poses.length} poses, ${s.rounds} ${s.rounds === 1 ? 'round' : 'rounds'}, ${pacing}` : `${poses.length} poses, ${pacing}`;
  }
  if (p.free) return `Bell every ${s.bellInterval >= 60 ? `${s.bellInterval / 60} min` : `${s.bellInterval}s`}`;
  return p.note ?? p.blurb;
}

interface Props {
  practice: Practice;
  onBegin: () => void;
  /** Hide the heading block when the parent already shows it. */
  compact?: boolean;
}

export const StartPanel = ({ practice, onBegin, compact }: Props) => {
  const [settings, setSettings] = useState(() => loadSettings(practice));
  const [customizing, setCustomizing] = useState(false);

  const update = (patch: Partial<Settings>) => {
    saveSettings(practice.id, patch);
    setSettings(prev => ({ ...prev, ...patch }));
  };

  const timeline = buildTimeline(practice, settings);
  const chips = lengthOptions(practice, settings);
  const current = chips?.kind === 'rounds' ? settings.rounds : settings.minutes;

  return (
    <div className={compact ? undefined : styles.startPanel}>
      {chips ? (
        <Chips
          label="Length"
          value={current}
          onChange={v => update(chips.kind === 'rounds' ? { rounds: v } : { minutes: v })}
          options={chips.options}
        />
      ) : (
        <p className={styles.natural}>{formatLength(timeline.total)}</p>
      )}
      <p className="eyebrow-soft" style={{ marginTop: 10 }}>
        {describe(practice, settings)}
      </p>

      <button className={`pill-button ${styles.begin}`} onClick={onBegin}>
        Begin
      </button>
      <button className="text-button" onClick={() => setCustomizing(true)}>
        Customize
      </button>

      {customizing && (
        <Customize
          practice={practice}
          settings={settings}
          onChange={update}
          onReset={() => setSettings(loadSettings(practice))}
          onClose={() => setCustomizing(false)}
        />
      )}
    </div>
  );
};
